import { createClient } from "../supabase/client";
import { getUrl } from "../utils";

// TODO(yash): Implement user onboarding on sign up and save name and other details in the profile table.
// With apple sign in, we don't get the name of the user, so we need to redirect them to a onboarding page after sign up to collect the name and other details.

interface UserOperationResponse {
	success: boolean;
	error: Error | null;
}

interface OAuthProps {
	provider: "apple" | "google";
	nextUrl?: string;
}

export async function signUpWithEmailPassword(
	email: string,
	password: string,
): Promise<UserOperationResponse> {
	const supabase = createClient();
	const { error } = await supabase.auth.signUp({
		email,
		password,
		options: {
			emailRedirectTo: `${getUrl()}/auth/sign-in`,
		},
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function signIn(
	email: string,
	password: string,
): Promise<UserOperationResponse> {
	const supabase = createClient();
	const { error } = await supabase.auth.signInWithPassword({
		email,
		password,
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function signInWithPhone(
	phone: string,
): Promise<UserOperationResponse> {
	const supabase = createClient();
	const { error } = await supabase.auth.signInWithOtp({
		phone,
		options: {
			shouldCreateUser: true,
		},
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function signInWithEmail(email: string) {
	const supabase = createClient();
	const { data, error } = await supabase.auth.signInWithOtp({
		email,
		options: {
			// shouldCreateUser: true,
			emailRedirectTo: `${getUrl()}/auth/sign-in`,
		},
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, data, error: null };
}

export async function signInWithOAuth({ provider, nextUrl = "/" }: OAuthProps) {
	const supabase = createClient();
	const { error } = await supabase.auth.signInWithOAuth({
		provider,
		// options: {
		// 	redirectTo: `${getSupabaseRedirectUrl()}?next=${nextUrl}`,
		// },
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function signOut(): Promise<UserOperationResponse> {
	const supabase = createClient();
	const { error } = await supabase.auth.signOut();
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function verifySmsOtp({
	phone,
	otp,
}: { phone: string; otp: string }) {
	const supabase = createClient();
	const {
		data: { session },
		error,
	} = await supabase.auth.verifyOtp({
		phone: phone,
		token: otp,
		type: "sms",
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, session, error: null };
}

export async function verifyEmailOtp({
	email,
	otp,
}: { email: string; otp: string }) {
	const supabase = createClient();
	const {
		data: { session },
		error,
	} = await supabase.auth.verifyOtp({
		email: email,
		token: otp,
		type: "email",
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, session, error: null };
}

export async function updatePassword(
	newPassword: string,
	currentPassword?: string,
): Promise<UserOperationResponse> {
	const supabase = createClient();
	const { error } = await supabase.rpc("update_password", {
		current_password_param: currentPassword || null,
		new_password_param: newPassword,
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function forgotPassword(
	email: string,
): Promise<UserOperationResponse> {
	const supabase = createClient();
	const { error } = await supabase.auth.resetPasswordForEmail(email, {
		// The url which will be included in the email.
		// This URL needs to be configured in your redirect URLs in the Supabase dashboard at
		// https://supabase.com/dashboard/project/_/auth/url-configuration
		redirectTo: `${getUrl()}/auth/update-password`,
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function checkEmailExist(email: string) {
	const supabase = createClient();
	const { error } = await supabase.rpc("email_exists", {
		email_param: email,
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function checkPasswordExist(userId: string) {
	const supabase = createClient();
	const { data, error } = await supabase.rpc("password_exists", {
		user_id_param: userId,
	});
	if (error) {
		return { exist: false, error };
	}
	if (data) {
		const exist = data as boolean;
		return { exist: exist, error: null };
	}
	return { exist: false, error: null };
}

export async function updateEmail(email: string) {
	const supabase = createClient();
	const { data, error } = await supabase.auth.updateUser({ email });
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function verifyPassword(password: string) {
	const supabase = createClient();
	const { error } = await supabase.rpc("verify_password", {
		password_param: password,
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function updatePhone(phone: string) {
	const supabase = createClient();
	const { data, error } = await supabase.auth.updateUser({ phone });
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function updatePhoneWithoutVerification(phone: string) {
	const supabase = createClient();
	const { data, error } = await supabase.auth.updateUser({
		phone: phone,
		// Store phone in metadata as backup
		data: {
			phone_number: phone,
			phone_verified: false,
		},
	});
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}

export async function deleteCurrentAccount() {
	const supabase = createClient();
	const { error } = await supabase.rpc("delete_current_user");
	if (error) {
		return { success: false, error };
	}
	return { success: true, error: null };
}
